"use client";
import {
  DividerComponent,
  LoaderComponent,
  PaperComponent,
  SliderComponent,
} from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  CurrencyConverter,
  GET_PRODUCT_APPLICATION,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import {
  useApplyLoanMutation,
  useGetApplicationQuery,
  useGetCustomerQuery,
  useGetOffersQuery,
  useProductDetailQuery,
  useSelectedOfferTenureMutation,
  useUpdateInsuranceMutation,
  useUpdateOfferSliderMutation,
} from "@/lib/redux";
import { setOfferSelectedData } from "@/lib/redux/slice";
import { setSelectedPlans } from "@/lib/redux/slice/InsuranceSlice";
import InsurancePlan from "@/screens/protection-plans";
import { InfoRounded } from "@mui/icons-material";
import {
  Box,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import TenureSelect from "./tenure-select";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { checkForRpl } from "@/lib/utils/rpl.utils";
import { useDebouncedCallback } from "@/lib/hooks/common.hooks";

const UpdateLoanAmountOffer = ({ product }) => {
  const router = useRouter();
  const [amount, setAmount] = useState(0);
  const dispatch = useDispatch();
  const [tenureEmi, setTenureEmi] = useState([]);
  const [selectedTenureEmi, setSelectedTenureEmi] = useState([]);
  const searchParams = useSearchParams();
  const offerId = searchParams.get("offerId");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedItem, setSelectedItem] = useState();
  const [sliderMinAmount, setSliderMinAmount] = useState(0);
  const [offerReminder, setOfferReminder] = useState(false);
  const [selectedOfferAmount, setSelectedOfferAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { insuranceDeclaration, insuranceSelected, selectedPlans, totalPlans } =
    useSelector((s) => s.insurance);
  const { offerSelectedData } = useSelector((s) => s.offerselection);
  const { setActions } = useFooter();
  const {
    data: productData,
    isLoading: fetchingProductDetails,
    error: detailsFetchError,
  } = useProductDetailQuery({ product });
  const {
    data: customerData,
    isLoading: fetchingCustomerData,
    error: errorCustomerData,
    isSuccess: customerDatafetched,
  } = useGetCustomerQuery("");
  const {
    data: applicationData,
    isLoading: applicationLoading,
    error: errorGetApplication,
    refetch: refetchApplicationData,
  } = useGetApplicationQuery("", { refetchOnMountOrArgChange: true });

  const [
    applyLoan,
    { data: applyLoanData, isLoading: applyingForLoan, error: applyingError },
  ] = useApplyLoanMutation();

  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const isRPL = checkForRpl(productApplication);
  const {
    data: offersData,
    error: errorOffers,
    isFetching: isfetchingOffer,
  } = useGetOffersQuery(
    {
      appId: productApplication?.appId,
      isRPL,
    },
    {
      skip: !productApplication?.appId,
      refetchOnMountOrArgChange: true,
    },
  );

  const [
    updateOfferSliderFunction,
    {
      data: updateOfferSliderData,
      error: errorUpdateOfferSlider,
      isLoading: UpdateOfferSliderLoading,
      isSuccess: isSuccessUpdateOfferSlider,
    },
  ] = useUpdateOfferSliderMutation();

  const updateOfferSlider = useDebouncedCallback(updateOfferSliderFunction);

  const [
    selectedOfferTenure,
    {
      data: selectedOfferTenureData,
      error: errorSelectedOfferTenure,
      isLoading: selectedOfferTenureLoading,
      isSuccess: selectedOfferTenureSuccess,
    },
  ] = useSelectedOfferTenureMutation();

  const [
    insuranceUpdate,
    {
      data: insuranceUpdateData,
      isLoading: loadingInsuranceUpdateData,
      error: insuranceUpdateDataError,
    },
  ] = useUpdateInsuranceMutation();

  // ************************************EFFECTS***********************************

  const isError =
    insuranceUpdateDataError ||
    errorSelectedOfferTenure ||
    errorUpdateOfferSlider ||
    errorOffers ||
    errorGetApplication ||
    detailsFetchError ||
    errorCustomerData;

  useErrorHandling(isError);
  useEffect(() => {
    if (offersData?.data) {
      const newSelectedItem = offersData?.data?.offers?.find(
        (item) => item?.id === Number(offerId),
      );
      const minAmount = offersData?.data?.offers?.find(
        (item) => item?.offerType === "APPLIED",
      )?.amount;
      setSliderMinAmount(minAmount);
      setAmount(
        offerSelectedData?.amount
          ? offerSelectedData?.amount
          : newSelectedItem?.amount,
      );
      setSelectedItem(newSelectedItem);
      updateOfferSlider({
        body: {
          appId: productApplication?.appId,
          offerId: newSelectedItem?.id,
          newRequestLoanAmount: offerSelectedData?.amount
            ? offerSelectedData?.amount
            : newSelectedItem?.amount,
        },
        isRPL: checkForRpl(productApplication),
      });
    }
  }, [offersData?.data, productApplication]);

  useEffect(() => {
    setIsLoading(
      loadingInsuranceUpdateData ||
        selectedOfferTenureLoading ||
        UpdateOfferSliderLoading ||
        applicationLoading ||
        fetchingCustomerData,
    );
  }, [
    loadingInsuranceUpdateData,
    selectedOfferTenureLoading,
    UpdateOfferSliderLoading,
    applicationLoading,
    fetchingCustomerData,
  ]);

  useEffect(() => {
    if (updateOfferSliderData?.data?.offerDetailsWithRoi) {
      const { amount, id, interestRate, offerType, slider, processingFee } =
        updateOfferSliderData?.data?.offerDetailsWithRoi;
      const updateOffer = {
        amount,
        id,
        interestRate,
        offerType,
        tenure: selectedTenureEmi?.value,
        emi: selectedTenureEmi?.name,
        slider,
        processingFee,
      };
      dispatch(setOfferSelectedData(updateOffer));
    }
  }, [
    updateOfferSliderData,
    selectedTenureEmi?.value,
    selectedTenureEmi?.name,
  ]);

  useEffect(() => {
    if (updateOfferSliderData?.data?.offerDetailsWithRoi?.reason) {
      setActions({
        disabledConfirmBtn: true,
      });
      return;
    }
    if (updateOfferSliderData) {
      setActions({
        disabledConfirmBtn: false,
      });
      let newtenureEmi =
        updateOfferSliderData?.data?.offerDetailsWithRoi?.tenureEMI;
      newtenureEmi = newtenureEmi?.map((item, index) => {
        return {
          id: index,
          value: `${item?.tenure}`,
          name: CurrencyConverter(item?.emi),
        };
      });
      let updateOfferSliderDataList =
        updateOfferSliderData?.data?.offerDetailsWithRoi?.tenureEMI;
      updateOfferSliderDataList = updateOfferSliderDataList?.filter(
        (item, index) =>
          CurrencyConverter(item?.emi) === selectedTenureEmi?.name,
      );
      let newSelectedItem;
      if (updateOfferSliderDataList?.length) {
        newSelectedItem = {
          value: `${updateOfferSliderDataList[0]?.tenure}`,
          name: CurrencyConverter(updateOfferSliderDataList[0]?.emi),
        };
      } else {
        newSelectedItem = newtenureEmi?.[0];
      }
      setTenureEmi(newtenureEmi);
      setSelectedTenureEmi(newSelectedItem);
    }
  }, [updateOfferSliderData]);

  useEffect(() => {
    if (selectedOfferTenureData?.data?.stage) {
      refetchApplicationData();
    }
  }, [selectedOfferTenureData]);

  useEffect(() => {
    if (offerSelectedData && Object?.keys(offerSelectedData)?.length) {
      const newSelectedItem = {
        name:
          typeof offerSelectedData?.emi === "number"
            ? CurrencyConverter(offerSelectedData?.emi)
            : offerSelectedData?.emi,
        value: offerSelectedData?.tenure,
      };
      setSelectedTenureEmi(newSelectedItem);
    }
  }, []);

  useEffect(() => {
    const stage = productApplication?.stage;
    if (stage && selectedOfferTenureData) {
      router.push(STAGE_BASED_ROUTING(stage));
    } else if (stage && stage !== STAGE?.OFFERS) {
      router.push(STAGE_BASED_ROUTING(stage));
    }
  }, [selectedOfferTenureData, productApplication?.stage]);

  useEffect(() => {
    setActions({
      showReject: true,
      showConfirm: offersData?.data?.offers?.length > 0,
      onConfirm: insuranceSelected
        ? proceedWithSelection
        : handleLimitedOfferModal,
      disabledConfirmBtn:
        (insuranceSelected && !insuranceDeclaration) ||
        updateOfferSliderData?.data?.offerDetailsWithRoi?.reason,
      confirmBtnLabel: "Accept The Offer",
      onReject: proceedWithoutSelection,
      rejectBtnLabel: "Back",
    });
  }, [
    setActions,
    productApplication,
    offersData,
    updateOfferSliderData,
    insuranceSelected,
    insuranceDeclaration,
    selectedTenureEmi?.name,
  ]);

  const handleCalculateEmi = (_, newValue) => {
    setAmount(newValue);
    updateOfferSlider({
      body: {
        appId: productApplication?.appId,
        offerId: selectedItem?.id,
        newRequestLoanAmount: newValue,
      },
      isRPL: checkForRpl(productApplication),
    });
  };
  const handleLimitedOfferModal = () => {
    setOfferReminder((o) => !o);
  };

  const proceedWithSelection = async () => {
    try {
      setIsLoading(true);

      const updateOfferTenure = {
        appId: productApplication?.appId,
        offerId: selectedItem?.id,
        newRequestLoanAmount: offerSelectedData?.amount.toString(),
        selectedTenure: parseInt(selectedTenureEmi?.value),
      };

      sendEventAnalytics({
        eventName: isRPL
          ? WEB_EVENTS.RL_OFFER_ACCEPTED
          : WEB_EVENTS.SELECTED_LOAN_AMOUNT_AGREE,
        ...productApplication,
        ...customerData?.data,
      });

      sendEventAnalytics({
        eventName: WEB_EVENTS.OFFER_SCREEN_INSURANCE_OPTED,
        ...productApplication,
        ...customerData?.data,
      });

      let insurancePayload;

      if (offerReminder) {
        const newSelectedPlans = Array.from(
          { length: totalPlans },
          (_, i) => i + 1,
        );

        insurancePayload = {
          appId: productApplication.appId,
          selectedPlan: newSelectedPlans,
          selectOfferRequest: {
            offerId: offerSelectedData?.id,
            newRequestLoanAmount: offerSelectedData?.amount,
            selectedTenure: offerSelectedData?.tenure,
          },
          insuranceConsent: false,
          isConsentRequest: false,
        };

        dispatch(setSelectedPlans(newSelectedPlans));
        setOfferReminder(false);
      } else {
        insurancePayload = {
          appId: productApplication.appId,
          selectedPlan: selectedPlans,
          selectOfferRequest: {
            offerId: offerSelectedData?.id,
            newRequestLoanAmount: offerSelectedData?.amount,
            selectedTenure: offerSelectedData?.tenure,
          },
          insuranceConsent: true,
          isConsentRequest: true,
        };
      }

      await insuranceUpdate(insurancePayload).unwrap();

      await selectedOfferTenure({ body: updateOfferTenure, isRPL }).unwrap?.();
    } catch (error) {
      console.error("proceedWithSelection failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueWithoutInsurance = async () => {
    setOfferReminder(false);
    let updateOfferTenure = {
      appId: productApplication?.appId,
      offerId: selectedItem?.id,
      newRequestLoanAmount: selectedItem?.slider
        ? (offerSelectedData?.amount).toString()
        : selectedItem?.amount,
      selectedTenure: parseInt(selectedTenureEmi?.value),
    };

    sendEventAnalytics({
      eventName: isRPL
        ? WEB_EVENTS.RL_OFFER_ACCEPTED
        : WEB_EVENTS.SELECTED_LOAN_AMOUNT_AGREE,
      ...productApplication,
      ...customerData?.data,
    });
    sendEventAnalytics({
      eventName: WEB_EVENTS.OFFER_SCREEN_INSURANCE_SKIPPED,
      ...productApplication,
      ...customerData?.data,
    });

    try {
      setIsLoading(true);
      await selectedOfferTenure({ body: updateOfferTenure, isRPL }).unwrap();
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const proceedWithoutSelection = async () => {
    setIsLoading(true);
    router.push(`/${product}/offers`);
  };
  return (
    <Stack>
      {selectedItem?.slider && (
        <Typography fontSize={{ md: "20px", xs: "16px" }} color={"#1F1F1FCC"}>
          How much money do you need?
        </Typography>
      )}
      <Box
        display="flex"
        flexDirection="column"
        justifyContent={"space-between"}
        flexGrow={1}
      ></Box>
      <Stack
        flexGrow={1}
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            my: { xs: 0, md: 2 },
            px: { xs: 0, md: 3 },
            pb: { xs: 0, md: 2 },
            width: "100%",
            bgcolor: { md: WEB_COLORS.paleSkyBlue, xs: "#fff" },
            borderRadius: "12px",
            pt: !selectedItem?.slider ? 3 : 0,
          }}
        >
          <LoaderComponent open={isLoading} />

          <Stack gap={1}>
            {selectedItem?.slider && (
              <PaperComponent
                noDrop
                paperStyle={{
                  backgroundColor: {
                    md: WEB_COLORS.lightSkyBlue,
                    xs: "#fff",
                  },
                  border: "1px solid #7272721A",
                }}
              >
                <Stack
                  direction={"row"}
                  alignItems="center"
                  justifyContent="space-between"
                  color={WEB_COLORS.primary}
                  // mt={2}
                >
                  <Typography>Loan Amount</Typography>
                  <Typography>{CurrencyConverter(amount)}</Typography>
                </Stack>
                <Stack>
                  <SliderComponent
                    min={sliderMinAmount}
                    max={selectedItem?.amount}
                    value={amount}
                    step={10000}
                    amountRequired={true}
                    onChange={handleCalculateEmi}
                  />
                </Stack>
                <Typography variant="body2" color="error" align="center">
                  {updateOfferSliderData?.data?.offerDetailsWithRoi?.reason}
                </Typography>
              </PaperComponent>
            )}
            {!updateOfferSliderData?.data?.offerDetailsWithRoi?.reason && (
              <>
                <TenureSelect
                  label="Select Tenure"
                  value={selectedTenureEmi}
                  menuItems={tenureEmi}
                  onChange={setSelectedTenureEmi}
                />

                <PaperComponent
                  noDrop
                  paperStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #7272721A",
                  }}
                >
                  <Stack
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    color={WEB_COLORS.primary}
                  >
                    <Typography>Loan Details</Typography>
                  </Stack>
                  <>
                    <DividerComponent />
                    <Stack>
                      <Stack
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                      >
                        <Typography mt={2}>EMI</Typography>
                        <Typography mt={2} textAlign={"right"}>
                          {selectedTenureEmi?.name}
                        </Typography>
                      </Stack>
                      <Stack
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                      >
                        <Typography mt={2}>Interest Rate</Typography>
                        <Typography mt={2} textAlign={"right"}>
                          {
                            updateOfferSliderData?.data?.offerDetailsWithRoi
                              ?.interestRate
                          }
                          % per annum
                        </Typography>
                      </Stack>
                      <Stack
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                      >
                        <Typography mt={2}>Tech/Documentation Fee</Typography>
                        <Stack alignItems={"flex-end"}>
                          <Typography mt={2} textAlign={"right"}>
                            {
                              updateOfferSliderData?.data?.offerDetailsWithRoi
                                ?.techDocumentationChargeFees
                            }
                            % of loan amount
                          </Typography>
                          <Typography fontSize={12} textAlign={"right"}>
                            + 18% GST
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </>
                </PaperComponent>
              </>
            )}
          </Stack>
        </Paper>
        <Stack>
          <Stack flexDirection={"row"} alignItems={"center"} gap={0.5}>
            <InfoRounded
              sx={{ color: "#FFC700" }}
              fontSize={isMobile ? "extra-small" : "small"}
            />
            <Typography
              align="justify"
              fontWeight={400}
              fontSize={{ xs: "10px", md: "14px" }}
              color="#3F3F3FCC"
            >
              {`Note: There will be a processing fee of ${
                updateOfferSliderData?.data?.offerDetailsWithRoi
                  ?.processingFee ?? 0
              }% for all transactions.`}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack>
        <InsurancePlan
          offerReminder={offerReminder}
          handleLimitedOfferModal={handleLimitedOfferModal}
          handleContinueWithInsurance={proceedWithSelection}
          handleContinueWithoutInsurance={handleContinueWithoutInsurance}
        />
      </Stack>
    </Stack>
  );
};

export default UpdateLoanAmountOffer;
